import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, AlertTriangle } from 'lucide-react'; // Added AlertTriangle for potential messages
import { useAuth } from '../contexts/AuthContext';
import KeypadModal from '../components/evaluation/KeypadModal';
import PlayerList from '../components/evaluation/PlayerList';
import { useEvaluationSessionData } from '../hooks/useEvaluationSessionData';
import { usePlayerFiltering } from '../hooks/usePlayerFiltering';
import { usePlayerEvaluationForm } from '../hooks/usePlayerEvaluationForm';
import { fetchEvaluationsForPlayer, updateJersey } from '../services/playerService';
import EvaluationHeader from '../components/evaluation/EvaluationHeader';
import PlayerFilters from '../components/evaluation/PlayerFilters';
import SelectedPlayerCard from '../components/evaluation/SelectedPlayerCard';
import DuplicateJerseyModal from '../components/evaluation/DuplicateJerseyModal'; // Import the new modal
import { Player } from '../types/Player';
import { PlayerEvaluation } from '../types/playerEvaluation';
import isEqual from 'lodash/isEqual';

const EVALUATION_POLLING_INTERVAL = 120000;

function Evaluation() {
  const { evaluationId } = useParams<{ evaluationId: string }>();
  const navigate = useNavigate();
  const handleBack = () => navigate('/coach');
  const { user } = useAuth();
  const coachId = user?.id || '';

  const [isKeypadOpen, setIsKeypadOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [selectedPlayerEvaluations, setSelectedPlayerEvaluations] = useState<PlayerEvaluation[] | null>(null);
  const [isFetchingEvaluations, setIsFetchingEvaluations] = useState<boolean>(false);
  const evaluationPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentEvaluationsRef = useRef<PlayerEvaluation[] | null>(null);

  // State for duplicate jersey handling
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicatePlayers, setDuplicatePlayers] = useState<Player[]>([]);
  const [jerseyNumberForDuplicates, setJerseyNumberForDuplicates] = useState<string>('');
  const [quickFindMessage, setQuickFindMessage] = useState<string | null>(null); // For no-match feedback

  useEffect(() => {
    currentEvaluationsRef.current = selectedPlayerEvaluations;
  }, [selectedPlayerEvaluations]);

  const {
    evaluation,
    players,
    setPlayers,
    coaches,
    allAgeGroups,
    isLoading: evalLoading,
    error: evalError,
    loadPlayers
  } = useEvaluationSessionData(evaluationId);

  const handlePlayerListRefresh = useCallback(() => loadPlayers(false), [loadPlayers]);

  const {
    filteredPlayers,
    nameSearch,
    setNameSearch,
    positionFilter,
    setPositionFilter,
    filterAssessed,
    setFilterAssessed,
  } = usePlayerFiltering(players, coachId);

  const fetchSelectedPlayerEvaluations = useCallback(async (player: Player, isPolling = false) => {
    if (!isPolling) {
      setIsFetchingEvaluations(true);
      setSelectedPlayerEvaluations(null);
    }
    try {
      const evals = await fetchEvaluationsForPlayer(player.id);
      // Only update if data has actually changed
      if (!isEqual(currentEvaluationsRef.current, evals)) {
        setSelectedPlayerEvaluations(evals);
      }
    } catch (error) {
      console.error(`Error fetching evaluations for player ${player.id}:`, error);
      if (!isPolling) {
        setSelectedPlayerEvaluations([]); // Set empty on error for initial load
      }
    } finally {
      if (!isPolling) {
        setIsFetchingEvaluations(false);
      }
    }
  }, []);

  // Fetch evaluations when player or session changes
  useEffect(() => {
    // Clear any existing interval
    if (evaluationPollingIntervalRef.current) {
      clearInterval(evaluationPollingIntervalRef.current);
      evaluationPollingIntervalRef.current = null;
    }

    if (selectedPlayer) {
      fetchSelectedPlayerEvaluations(selectedPlayer); // Initial fetch
    } else {
      setSelectedPlayerEvaluations(null); // Clear evaluations if no player selected
      setIsFetchingEvaluations(false);
      currentEvaluationsRef.current = null; // Reset ref as well
    }

    // Cleanup interval on unmount or change
    return () => {
      if (evaluationPollingIntervalRef.current) {
        clearInterval(evaluationPollingIntervalRef.current);
        evaluationPollingIntervalRef.current = null;
      }
    };
  }, [selectedPlayer, evaluationId, fetchSelectedPlayerEvaluations]);

  // Set up polling *after* initial fetch completes
  useEffect(() => {
    // Clear previous interval if dependencies change
    if (evaluationPollingIntervalRef.current) {
      clearInterval(evaluationPollingIntervalRef.current);
      evaluationPollingIntervalRef.current = null;
    }

    if (selectedPlayer && !isFetchingEvaluations) { // Only poll if player selected and not currently fetching
      evaluationPollingIntervalRef.current = setInterval(() => {
        fetchSelectedPlayerEvaluations(selectedPlayer, true); // Indicate polling call
      }, EVALUATION_POLLING_INTERVAL);
    }

    // Cleanup interval on unmount or change
    return () => {
      if (evaluationPollingIntervalRef.current) {
        clearInterval(evaluationPollingIntervalRef.current);
        evaluationPollingIntervalRef.current = null;
      }
    };
  }, [selectedPlayer, isFetchingEvaluations, fetchSelectedPlayerEvaluations]);


  // Sync player list state after an evaluation is saved
  const syncPlayerEvaluationState = useCallback((savedEvaluation: PlayerEvaluation) => {
    // Update the master `players` list with the new evaluation status
    setPlayers(currentPlayers =>
      currentPlayers.map(p => {
        if (p.id === savedEvaluation.player_id) {
          const currentCoachIds = p.evaluatedByCoachIds || [];
          // Add coach ID if not already present
          if (!currentCoachIds.includes(savedEvaluation.coach_id)) {
            return { ...p, evaluatedByCoachIds: [...currentCoachIds, savedEvaluation.coach_id].sort() };
          }
        }
        return p;
      })
    );

    // Update the `selectedPlayerEvaluations` if the saved eval belongs to the selected player
    if (selectedPlayer && savedEvaluation.player_id === selectedPlayer.id) {
      setSelectedPlayerEvaluations(prevEvals => {
        const currentEvals = prevEvals || [];
        // Check if eval already exists (by ID if available, otherwise composite key)
        const existingIndex = currentEvals.findIndex(e =>
          (savedEvaluation.id && e.id === savedEvaluation.id) ||
          (e.player_id === savedEvaluation.player_id &&
           e.coach_id === savedEvaluation.coach_id &&
           e.evaluation_session_id === savedEvaluation.evaluation_session_id)
        );

        let newEvals;
        if (existingIndex > -1) {
          // Replace existing
          newEvals = [...currentEvals];
          newEvals[existingIndex] = savedEvaluation;
        } else {
          // Add new
          newEvals = [...currentEvals, savedEvaluation];
        }
        // Ensure consistent keys (prefer evaluation_session_id)
        return newEvals.map(ev => ({...ev, evaluation_session_id: ev.evaluation_id || ev.evaluation_session_id }));
      });
    }
  }, [setPlayers, selectedPlayer]); // Dependencies

  // Hook to manage the evaluation form state and saving logic
  const playerEvaluationForm = usePlayerEvaluationForm(
    selectedPlayer,
    evaluationId,
    coachId,
    syncPlayerEvaluationState,
    selectedPlayerEvaluations, // Pass the fetched evaluations
    isFetchingEvaluations     // Pass the loading state
  );

  // Effect to update selected player data if the master list changes
  useEffect(() => {
    if (selectedPlayer) {
      const latestPlayerData = players.find(p => p.id === selectedPlayer.id);
      if (latestPlayerData && !isEqual(latestPlayerData, selectedPlayer)) {
        // Update if data differs
        setSelectedPlayer(latestPlayerData);
      } else if (!latestPlayerData && selectedPlayer) {
        // Deselect if player removed from list
        setSelectedPlayer(null);
      }
    }
  }, [players, selectedPlayer?.id]); // Run when players list or selected player ID changes

  const handleOpenKeypad = () => {
      setQuickFindMessage(null); // Clear any previous message
      setIsKeypadOpen(true);
  }
  const handleCloseKeypad = () => setIsKeypadOpen(false);

  // Handle submission from the jersey number keypad
  const handleJerseySubmit = (jerseyNumber: string) => {
    setIsKeypadOpen(false); // Close keypad immediately
    setQuickFindMessage(null); // Clear previous message

    // Parse the input jersey number to an integer
    const targetJerseyInt = parseInt(jerseyNumber, 10);

    // Validate parsed number (ensure it's not NaN)
    if (isNaN(targetJerseyInt)) {
      console.error(`Invalid jersey number input: ${jerseyNumber}`);
      setQuickFindMessage(`Invalid jersey number: ${jerseyNumber}.`);
      return; // Stop processing if input is not a valid number
    }

    // Find ALL players matching the target jersey number (comparing numerically)
    const matchingPlayers = players.filter((p: Player) => {
      // Ensure player has a jersey number and parse it to an integer
      const playerJerseyInt = p.jerseyNumber ? parseInt(p.jerseyNumber, 10) : NaN;
      // Compare integer values, handle NaN safely
      return !isNaN(playerJerseyInt) && playerJerseyInt === targetJerseyInt;
    });

    if (matchingPlayers.length === 1) {
      // Only one match found - select directly
      setSelectedPlayer(matchingPlayers[0]);
      window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top
      setDuplicatePlayers([]);
      setIsDuplicateModalOpen(false);
    } else if (matchingPlayers.length > 1) {
      // Multiple matches found - open duplicate selection modal
      setDuplicatePlayers(matchingPlayers);
      setJerseyNumberForDuplicates(jerseyNumber); // Store the original input number for the modal title
      setIsDuplicateModalOpen(true);
      setSelectedPlayer(null); // Deselect any current player while choosing
    } else {
      // No matches found
      console.warn(`Player with jersey #${jerseyNumber} not found.`);
      setSelectedPlayer(null); // Ensure no player is selected
      setDuplicatePlayers([]);
      setIsDuplicateModalOpen(false);
      setQuickFindMessage(`No player found with jersey #${jerseyNumber}.`); // Use original input for message
    }
  };

  // Handle selecting a player from the duplicate jersey modal
  const handleSelectDuplicatePlayer = (playerId: string) => {
    const playerToSelect = duplicatePlayers.find(p => p.id === playerId);
    if (playerToSelect) {
      setSelectedPlayer(playerToSelect);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsDuplicateModalOpen(false); // Close the modal
    setDuplicatePlayers([]); // Clear the duplicates list
    setJerseyNumberForDuplicates('');
  };

  // Handle closing the duplicate jersey modal without selection
  const handleCloseDuplicateModal = () => {
    setIsDuplicateModalOpen(false);
    setDuplicatePlayers([]);
    setJerseyNumberForDuplicates('');
    // Optionally keep the quickFindMessage if needed
  };

  // Handle selecting a player directly from the player list
  const handlePlayerSelect = (playerId: string) => {
    const player = players.find(p => p.id === playerId) || null;
    // Only update state if the selected player actually changes
    if (player?.id !== selectedPlayer?.id) {
      setQuickFindMessage(null); // Clear quick find message on list selection
      setSelectedPlayer(player);
    }
  };

  // Handle updating a player's jersey number and color (via modal in SelectedPlayerCard)
  const handleUpdateJersey = async (
    playerId: string,
    newJerseyNumber: string,
    newJerseyColor: import('../types/Player').JerseyColor
  ) => {
    if (!evaluationId) return;

    try {
      await updateJersey(playerId, evaluationId, newJerseyNumber, newJerseyColor);
      setPlayers(prevPlayers =>
        prevPlayers.map(p =>
          p.id === playerId
            ? { ...p, jerseyNumber: newJerseyNumber, jerseyColor: newJerseyColor }
            : p
        )
      );
      if (selectedPlayer && selectedPlayer.id === playerId) {
        setSelectedPlayer(prev =>
          prev ? { ...prev, jerseyNumber: newJerseyNumber, jerseyColor: newJerseyColor } : null
        );
      }
    } catch (error) {
      console.error('Failed to update jersey:', error);
    }
  };

  // Loading state for the main evaluation session data
  if (evalLoading && !evaluation) {
    return <div className="flex justify-center items-center min-h-screen">Loading Session...</div>;
  }

  // Error state for the main evaluation session data
  if (evalError && !evaluation) {
    return <div className="text-red-500 p-4 text-center mt-10">Error loading evaluation session: {evalError || 'Session could not be loaded.'}</div>;
  }
  if (!evaluation && !evalLoading) {
    return <div className="text-gray-500 p-4 text-center mt-10">Evaluation session not found or ID is invalid.</div>;
  }

  // Main render
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <EvaluationHeader
        evaluation={evaluation}
        isLoading={evalLoading}
        error={evalError}
        onOpenKeypad={handleOpenKeypad}
        onBack={handleBack}
      />

      {/* Main Content Area */}
      <main className="flex-grow container mx-auto p-4 lg:p-6">

        {/* Quick Find Feedback Message */}
        {quickFindMessage && (
          <div className="mb-4 p-3 border border-yellow-300 bg-yellow-50 rounded-lg flex items-center text-yellow-700">
            <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
            <p>{quickFindMessage}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Left/Main Panel: Selected Player Details & Evaluation Form */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-md p-6 space-y-6">
            {selectedPlayer ? (
              <SelectedPlayerCard
                key={selectedPlayer.id} // Ensure re-render on player change
                selectedPlayer={selectedPlayer}
                evaluationId={evaluationId || ''}
                coachId={coachId}
                coaches={coaches}
                evaluation={evaluation}
                allAgeGroups={allAgeGroups}
                myEvaluationFormData={playerEvaluationForm.myEvaluation}
                allPlayerEvaluations={selectedPlayerEvaluations}
                isLoadingEvaluations={isFetchingEvaluations}
                handleScoreChange={playerEvaluationForm.handleScoreChange}
                handleNotesChange={playerEvaluationForm.handleNotesChange}
                handleTeamRecommendationChange={playerEvaluationForm.handleTeamRecommendationChange}
                saveStatus={playerEvaluationForm.saveStatus}
                isDirty={playerEvaluationForm.isDirty}
                saveError={playerEvaluationForm.saveError}
                onUpdateJersey={handleUpdateJersey}
              />
            ) : (
              // Placeholder when no player is selected
              <div className="text-center py-12 text-gray-500">
                <User className="h-12 w-12 mx-auto mb-4" />
                <p>Select a player from the list or use Quick Find (#) to begin evaluation</p>
              </div>
            )}
          </div>

          {/* Right Panel: Player List & Filters */}
          <div className="bg-white rounded-xl shadow-md p-6 flex flex-col">
            {evaluation && ( // Only show list/filters if evaluation session is loaded
              <>
                <PlayerFilters
                  nameSearch={nameSearch}
                  setNameSearch={setNameSearch}
                  positionFilter={positionFilter}
                  setPositionFilter={setPositionFilter}
                  filterAssessed={filterAssessed}
                  setFilterAssessed={setFilterAssessed}
                />
                <div className="mt-4 flex-grow overflow-y-auto">
                  <PlayerList
                    players={filteredPlayers}
                    selectedPlayer={selectedPlayer}
                    onSelect={handlePlayerSelect}
                    coachId={coachId}
                    coaches={coaches}
                    allAgeGroups={allAgeGroups}
                    onRefresh={handlePlayerListRefresh}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      <KeypadModal
        isOpen={isKeypadOpen}
        onClose={handleCloseKeypad}
        onSubmit={handleJerseySubmit}
      />
      <DuplicateJerseyModal
        isOpen={isDuplicateModalOpen}
        onClose={handleCloseDuplicateModal}
        onSelect={handleSelectDuplicatePlayer}
        players={duplicatePlayers}
        jerseyNumber={jerseyNumberForDuplicates}
      />
    </div>
  );
}

export default Evaluation;